import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getOptimizedImageUrl } from "@/lib/utils";
import { getAboutContent } from "@/lib/content";
import type { AboutContent } from "@/types/content";

const AboutTeaser = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [content, setContent] = useState<AboutContent | null>(null);

  useEffect(() => {
    getAboutContent(setContent).then(setContent);
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <section className="py-20 md:py-28 bg-[#FAF8F5] overflow-hidden border-t border-border/40">
      <div className="container px-4 md:px-8">
        <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16 lg:gap-24">
          {/* Left: Image Side */}
          <div className="w-full md:w-1/2 relative group">
            <div className="relative z-10 rounded-none w-full flex justify-center">
              <video
                src="/about-video.mp4"
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-auto max-h-[85vh] object-contain"
              />
            </div>
          </div>

          {/* Right: Text Side */}
          <div className="w-full md:w-1/2 space-y-6 sm:space-y-8">
            <div className="space-y-2 sm:space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground font-body font-light text-[10px] tracking-[0.25em] uppercase">THE ORIGIN STORY</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-display font-light text-foreground tracking-[0.25em] uppercase leading-[1.3]">
                By Designers<br />
                <span className="text-muted-foreground/60 font-light">With Care,</span><br />
                For You.
              </h2>
            </div>

            <p className="text-foreground/80 text-xs sm:text-sm font-body font-light tracking-wide leading-relaxed">
              {content?.hero?.description || "We set out with a simple vision: to create a premium, minimalist brand that makes high-quality everyday essentials affordable and sustainably made."}
            </p>

            <div className="pt-2 sm:pt-4 flex flex-col sm:flex-row items-start gap-6">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-[#171717] hover:bg-[#2A2A2A] text-white px-8 py-3.5 rounded-none font-body font-light text-xs uppercase tracking-[0.2em] transition-colors shadow-none active:scale-98 group"
              >
                Read Our Full Story
                <ArrowRight size={14} className="group-hover:translate-x-1.5 transition-transform duration-300" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutTeaser;
